'use client';

import React, { forwardRef } from 'react';
import './Card.css';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Card content */
  children: React.ReactNode;
  /** Optional padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Whether to add hover effect */
  hoverable?: boolean;
  /** Whether the card is selected/active */
  selected?: boolean;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Header content */
  children: React.ReactNode;
  /** Actions to display on the right side of header */
  actions?: React.ReactNode;
}

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Body content */
  children: React.ReactNode;
  /** Override default padding */
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Footer content */
  children: React.ReactNode;
  /** Alignment of footer content */
  align?: 'left' | 'center' | 'right' | 'between';
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ children, className = '', padding = 'md', hoverable = false, selected = false, ...props }, ref) => {
    const classes = [
      'ds-card',
      `ds-card--padding-${padding}`,
      hoverable && 'ds-card--hoverable',
      selected && 'ds-card--selected',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ children, className = '', actions, ...props }, ref) => {
    return (
      <div ref={ref} className={`ds-card__header ${className}`} {...props}>
        <div className="ds-card__header-content">{children}</div>
        {actions && <div className="ds-card__header-actions">{actions}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement> & { children: React.ReactNode }>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <h3 ref={ref} className={`ds-card__title ${className}`} {...props}>
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ children, className = '', padding, ...props }, ref) => {
    const classes = [
      'ds-card__body',
      padding && `ds-card__body--padding-${padding}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, className = '', align = 'right', ...props }, ref) => {
    return (
      <div ref={ref} className={`ds-card__footer ds-card__footer--${align} ${className}`} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardBody, CardFooter };
